import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import Navbar from './Navbar';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiPlus, FiTrash2, FiEdit, FiUpload, FiPlay, FiPause, FiRefreshCw, FiInfo, FiSettings, FiX, FiChevronDown, FiChevronUp, FiCopy, FiCheck, FiGrid, FiList } = FiIcons;

const SyncGroupManager = () => {
  const [syncGroups, setSyncGroups] = useState([
    {
      id: 'lobby-wall',
      name: 'Lobby Video Wall',
      totalScreens: 3,
      orientation: 'horizontal',
      connectedScreens: 2,
      status: 'active',
      currentContent: 'Spring Promotion',
      lastSync: '5 minutes ago',
      syncMode: 'manual', // 'manual' or 'auto'
      screens: [
        { id: 1, name: 'Left Screen', status: 'online', pairingCode: 'LB1234' },
        { id: 2, name: 'Middle Screen', status: 'online', pairingCode: 'LB5678' },
        { id: 3, name: 'Right Screen', status: 'offline', pairingCode: 'LB9012' }
      ]
    },
    {
      id: 'conference-room',
      name: 'Conference Room Display',
      totalScreens: 2,
      orientation: 'horizontal',
      connectedScreens: 0,
      status: 'offline',
      currentContent: 'Company Overview',
      lastSync: '2 days ago',
      syncMode: 'auto', // 'manual' or 'auto'
      screens: [
        { id: 1, name: 'Left Screen', status: 'offline', pairingCode: 'CR1234' },
        { id: 2, name: 'Right Screen', status: 'offline', pairingCode: 'CR5678' }
      ]
    }
  ]);

  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfigureScreensModal, setShowConfigureScreensModal] = useState(false);
  const [showAddScreenModal, setShowAddScreenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [newScreen, setNewScreen] = useState({ name: '' });
  const [uploadMode, setUploadMode] = useState('auto'); // 'auto' or 'manual'
  const [manualScreens, setManualScreens] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  const [newGroup, setNewGroup] = useState({
    name: '',
    totalScreens: 3,
    orientation: 'horizontal',
    syncMode: 'manual' // Default to manual sync mode
  });

  // Language settings
  const [language, setLanguage] = useState('en');
  const translations = {
    en: {
      title: 'Multi-Screen Sync Groups',
      subtitle: 'Manage synchronized displays for immersive content',
      createBtn: 'Create Sync Group',
      infoTitle: 'Multi-Screen Synchronization',
      infoDesc: 'Create immersive video walls by splitting content across multiple screens with perfect frame-level synchronization.',
      learnMore: 'Learn More',
      allGroups: 'All Sync Groups',
      noGroups: 'No sync groups created yet',
      createFirst: 'Create Your First Group',
      uploadContent: 'Upload Content',
      configureScreens: 'Configure Screens',
      deleteGroup: 'Delete Group',
      groupId: 'Group ID',
      lastSync: 'Last Synchronized',
      preview: 'Preview',
      viewLayout: 'View Layout',
      manualSync: 'Manual Sync',
      autoSync: 'Auto Split',
      currentContent: 'Current Content',
      connected: 'connected',
      screens: 'screens'
    },
    de: {
      title: 'Multi-Screen-Synchronisationsgruppen',
      subtitle: 'Verwalten Sie synchronisierte Anzeigen für immersive Inhalte',
      createBtn: 'Synchronisationsgruppe erstellen',
      infoTitle: 'Multi-Screen-Synchronisation',
      infoDesc: 'Erstellen Sie immersive Videowände, indem Sie Inhalte auf mehrere Bildschirme mit perfekter Frame-Level-Synchronisation verteilen.',
      learnMore: 'Mehr erfahren',
      allGroups: 'Alle Synchronisationsgruppen',
      noGroups: 'Noch keine Synchronisationsgruppen erstellt',
      createFirst: 'Erstellen Sie Ihre erste Gruppe',
      uploadContent: 'Inhalt hochladen',
      configureScreens: 'Bildschirme konfigurieren',
      deleteGroup: 'Gruppe löschen',
      groupId: 'Gruppen-ID',
      lastSync: 'Zuletzt synchronisiert',
      preview: 'Vorschau',
      viewLayout: 'Layout anzeigen',
      manualSync: 'Manuelle Synchronisierung',
      autoSync: 'Automatische Aufteilung',
      currentContent: 'Aktueller Inhalt',
      connected: 'verbunden',
      screens: 'Bildschirme'
    }
  };
  
  const t = translations[language];

  const handleAddGroup = () => {
    if (!newGroup.name) {
      toast.error('Please provide a name for the sync group');
      return;
    }

    const groupId = newGroup.name.toLowerCase().replace(/\s+/g, '-');
    
    // Create initial screens based on totalScreens
    const screens = [];
    for (let i = 1; i <= newGroup.totalScreens; i++) {
      const position = newGroup.orientation === 'horizontal' 
        ? (i === 1 ? 'Left' : i === newGroup.totalScreens ? 'Right' : 'Middle')
        : (i === 1 ? 'Top' : i === newGroup.totalScreens ? 'Bottom' : 'Middle');
      
      screens.push({
        id: i,
        name: `${position} Screen`,
        status: 'offline',
        pairingCode: generatePairingCode()
      });
    }
    
    const group = {
      id: groupId,
      name: newGroup.name,
      totalScreens: newGroup.totalScreens,
      orientation: newGroup.orientation,
      connectedScreens: 0,
      status: 'idle',
      currentContent: 'None',
      lastSync: 'Never',
      syncMode: newGroup.syncMode,
      screens: screens
    };

    setSyncGroups([...syncGroups, group]);
    setNewGroup({
      name: '',
      totalScreens: 3,
      orientation: 'horizontal',
      syncMode: 'manual'
    });
    setShowNewGroupModal(false);
    toast.success('Sync group created successfully!');
  };

  const handleDeleteGroup = (groupId) => {
    setSyncGroups(syncGroups.filter(group => group.id !== groupId));
    toast.success('Sync group deleted');
  };

  const handleUploadContent = () => {
    if (!selectedGroup) return;
    
    // In a real app, this would handle the actual content upload
    setTimeout(() => {
      const updatedGroups = syncGroups.map(group => {
        if (group.id === selectedGroup.id) {
          return {
            ...group,
            currentContent: uploadMode === 'auto' ? 'Auto-split Content' : 'Manually Assigned Content',
            lastSync: 'Just now'
          };
        }
        return group;
      });
      
      setSyncGroups(updatedGroups);
      setShowUploadModal(false);
      toast.success('Content uploaded successfully!');
    }, 1500);
  };

  const handleAddScreen = () => {
    if (!selectedGroup || !newScreen.name) {
      toast.error('Please enter a screen name');
      return;
    }

    const updatedGroups = syncGroups.map(group => {
      if (group.id === selectedGroup.id) {
        const newScreenObj = {
          id: group.screens.length + 1,
          name: newScreen.name,
          status: 'offline',
          pairingCode: generatePairingCode()
        };
        
        return {
          ...group,
          screens: [...group.screens, newScreenObj],
          totalScreens: group.totalScreens + 1
        };
      }
      return group;
    });

    setSyncGroups(updatedGroups);
    setNewScreen({ name: '' });
    setShowAddScreenModal(false);
    toast.success('Screen added successfully!');
  };

  const handleDeleteScreen = (groupId, screenId) => {
    const updatedGroups = syncGroups.map(group => {
      if (group.id === groupId) {
        const updatedScreens = group.screens.filter(screen => screen.id !== screenId);
        return {
          ...group,
          screens: updatedScreens,
          totalScreens: updatedScreens.length
        };
      }
      return group;
    });

    setSyncGroups(updatedGroups);
    toast.success('Screen removed from group');
  };

  const handleEditScreenName = (groupId, screenId, newName) => {
    const updatedGroups = syncGroups.map(group => {
      if (group.id === groupId) {
        const updatedScreens = group.screens.map(screen => {
          if (screen.id === screenId) {
            return { ...screen, name: newName };
          }
          return screen;
        });
        return { ...group, screens: updatedScreens };
      }
      return group;
    });

    setSyncGroups(updatedGroups);
  };

  const toggleExpandGroup = (groupId) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  const generatePairingCode = () => {
    const prefix = Math.random().toString(36).substring(2, 4).toUpperCase();
    const numbers = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}${numbers}`;
  };

  const copyPairingCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Pairing code copied to clipboard');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'idle': return 'text-yellow-400';
      case 'offline': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return FiPlay;
      case 'idle': return FiPause;
      case 'offline': return FiInfo;
      default: return FiInfo;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with language selector */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{t.title}</h1>
              <p className="text-white/70">
                {t.subtitle}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              
              <button 
                onClick={() => setShowNewGroupModal(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                <SafeIcon icon={FiPlus} />
                <span>{t.createBtn}</span>
              </button>
            </div>
          </motion.div>

          {/* Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h2 className="text-xl font-semibold text-white mb-2">{t.infoTitle}</h2>
                <p className="text-white/70 max-w-2xl">
                  {t.infoDesc}
                </p>
              </div>
              <button
                onClick={() => window.open('https://docs.signagecloud.com/multi-screen', '_blank')}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                <SafeIcon icon={FiInfo} />
                <span>{t.learnMore}</span>
              </button>
            </div>
          </motion.div>

          {/* Sync Groups List */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }} 
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/20 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">{t.allGroups}</h2>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setViewMode('grid')} 
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/10 text-white/70'}`}
                >
                  <SafeIcon icon={FiGrid} />
                </button>
                <button 
                  onClick={() => setViewMode('list')} 
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/10 text-white/70'}`}
                >
                  <SafeIcon icon={FiList} />
                </button>
              </div>
            </div>
            
            {syncGroups.length === 0 ? (
              <div className="py-12 text-center">
                <SafeIcon icon={FiMonitor} className="text-4xl text-white/30 mx-auto mb-4" />
                <p className="text-white/70">{t.noGroups}</p>
                <button 
                  onClick={() => setShowNewGroupModal(true)}
                  className="mt-4 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  {t.createFirst}
                </button>
              </div>
            ) : viewMode === 'list' ? (
              <div className="divide-y divide-white/10">
                {syncGroups.map((group) => (
                  <div key={group.id}>
                    <div className="p-6 hover:bg-white/5 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div 
                            className={`w-12 h-12 bg-gradient-to-br ${
                              group.status === 'active' 
                                ? 'from-green-400 to-emerald-400' 
                                : group.status === 'idle'
                                  ? 'from-yellow-400 to-orange-400'
                                  : 'from-red-400 to-pink-400'
                            } rounded-xl flex items-center justify-center`}
                          >
                            <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-lg">{group.name}</h3>
                            <div className="flex items-center space-x-4 text-sm text-white/70">
                              <span>{group.totalScreens} {t.screens} ({group.orientation})</span>
                              <div className="flex items-center space-x-1">
                                <SafeIcon 
                                  icon={getStatusIcon(group.status)} 
                                  className={`text-xs ${getStatusColor(group.status)}`} 
                                />
                                <span className={getStatusColor(group.status)}>
                                  {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                                </span>
                              </div>
                              <span>{group.connectedScreens}/{group.totalScreens} {t.connected}</span>
                              <span className="bg-white/10 px-2 py-1 rounded text-xs">
                                {group.syncMode === 'auto' ? t.autoSync : t.manualSync}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="text-right text-sm mr-4">
                            <p className="text-white/70">{t.currentContent}</p>
                            <p className="text-white">{group.currentContent}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => {
                                setSelectedGroup(group);
                                setShowUploadModal(true);
                              }}
                              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiUpload} className="text-white/70 hover:text-white" />
                            </button>
                            <button 
                              onClick={() => {
                                setSelectedGroup(group);
                                setShowConfigureScreensModal(true);
                              }}
                              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiSettings} className="text-white/70 hover:text-white" />
                            </button>
                            <button 
                              onClick={() => handleDeleteGroup(group.id)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="text-red-400" />
                            </button>
                            <button
                              onClick={() => toggleExpandGroup(group.id)}
                              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                            >
                              <SafeIcon 
                                icon={expandedGroup === group.id ? FiChevronUp : FiChevronDown} 
                                className="text-white/70 hover:text-white" 
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional group details */}
                      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/60 text-sm">{t.groupId}</p>
                          <p className="text-white font-mono">{group.id}</p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/60 text-sm">{t.lastSync}</p>
                          <p className="text-white">{group.lastSync}</p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 flex items-center justify-between">
                          <div>
                            <p className="text-white/60 text-sm">{t.preview}</p>
                          </div>
                          <button className="flex items-center space-x-1 text-sm text-purple-400 hover:text-purple-300 transition-colors">
                            <SafeIcon icon={FiPlay} className="text-xs" />
                            <span>{t.viewLayout}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded view with screen details */}
                    {expandedGroup === group.id && (
                      <div className="bg-white/5 p-4 mb-4 mx-6">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-white font-medium">Connected Screens</h4>
                          <button 
                            onClick={() => {
                              setSelectedGroup(group);
                              setShowAddScreenModal(true);
                            }}
                            className="flex items-center space-x-1 text-sm bg-white/10 px-3 py-1 rounded-lg hover:bg-white/20 transition-colors"
                          >
                            <SafeIcon icon={FiPlus} className="text-xs" />
                            <span>Add Screen</span>
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          {group.screens.map((screen) => (
                            <div 
                              key={screen.id} 
                              className="flex items-center justify-between bg-white/10 p-3 rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-2 h-2 rounded-full ${screen.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                <span className="text-white">{screen.name}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="bg-white/10 px-3 py-1 rounded-lg flex items-center space-x-2">
                                  <span className="text-white/70 text-sm">Code: {screen.pairingCode}</span>
                                  <button 
                                    onClick={() => copyPairingCode(screen.pairingCode)}
                                    className="text-purple-400 hover:text-purple-300"
                                  >
                                    <SafeIcon icon={FiCopy} className="text-xs" />
                                  </button>
                                </div>
                                <button 
                                  onClick={() => handleDeleteScreen(group.id, screen.id)}
                                  className="p-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                                >
                                  <SafeIcon icon={FiTrash2} className="text-red-400 text-sm" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {syncGroups.map((group) => (
                  <div 
                    key={group.id} 
                    className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all"
                  >
                    <div className="p-5">
                      <div className="flex items-center space-x-3 mb-4">
                        <div 
                          className={`w-10 h-10 bg-gradient-to-br ${
                            group.status === 'active' 
                              ? 'from-green-400 to-emerald-400' 
                              : group.status === 'idle'
                                ? 'from-yellow-400 to-orange-400'
                                : 'from-red-400 to-pink-400'
                          } rounded-xl flex items-center justify-center`}
                        >
                          <SafeIcon icon={FiMonitor} className="text-white text-lg" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{group.name}</h3>
                          <div className="flex items-center space-x-2 text-xs text-white/70">
                            <span>{group.totalScreens} {t.screens}</span>
                            <div className="w-1 h-1 bg-white/30 rounded-full"></div>
                            <span className={getStatusColor(group.status)}>
                              {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/70">Connected:</span>
                          <span className="text-white">{group.connectedScreens}/{group.totalScreens}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/70">Sync Mode:</span>
                          <span className="text-white">{group.syncMode === 'auto' ? 'Auto Split' : 'Manual'}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/70">Last Sync:</span>
                          <span className="text-white">{group.lastSync}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-center mb-4">
                        {group.orientation === 'horizontal' ? (
                          <div className="flex space-x-1">
                            {Array.from({ length: group.totalScreens }).map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-8 h-16 ${
                                  idx < group.connectedScreens 
                                    ? 'bg-purple-500/30 border-purple-400/50' 
                                    : 'bg-white/10 border-white/20'
                                } border rounded-md flex items-center justify-center`}
                              >
                                <span className="text-white/70 text-xs">{idx + 1}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col space-y-1">
                            {Array.from({ length: group.totalScreens }).map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-16 h-8 ${
                                  idx < group.connectedScreens 
                                    ? 'bg-purple-500/30 border-purple-400/50' 
                                    : 'bg-white/10 border-white/20'
                                } border rounded-md flex items-center justify-center`}
                              >
                                <span className="text-white/70 text-xs">{idx + 1}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => {
                            setSelectedGroup(group);
                            setShowUploadModal(true);
                          }}
                          className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white text-sm flex items-center justify-center space-x-1"
                        >
                          <SafeIcon icon={FiUpload} className="text-sm" />
                          <span>Upload</span>
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedGroup(group);
                            setShowConfigureScreensModal(true);
                          }}
                          className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white text-sm flex items-center justify-center space-x-1"
                        >
                          <SafeIcon icon={FiSettings} className="text-sm" />
                          <span>Configure</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Create New Group Modal */}
      {showNewGroupModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.3 }} 
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Create Sync Group</h2>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Group Name
                </label>
                <input 
                  type="text" 
                  value={newGroup.name} 
                  onChange={(e) => setNewGroup({...newGroup, name: e.target.value})} 
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent" 
                  placeholder="e.g., Lobby Video Wall" 
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Number of Screens
                </label>
                <select 
                  value={newGroup.totalScreens} 
                  onChange={(e) => setNewGroup({...newGroup, totalScreens: parseInt(e.target.value)})} 
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                >
                  {[2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num} Screens</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Orientation
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setNewGroup({...newGroup, orientation: 'horizontal'})}
                    className={`p-3 rounded-xl border text-center ${
                      newGroup.orientation === 'horizontal'
                        ? 'bg-purple-500/30 border-purple-400/50 text-white'
                        : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    Horizontal
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewGroup({...newGroup, orientation: 'vertical'})}
                    className={`p-3 rounded-xl border text-center ${
                      newGroup.orientation === 'vertical'
                        ? 'bg-purple-500/30 border-purple-400/50 text-white'
                        : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    Vertical
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Synchronization Mode
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setNewGroup({...newGroup, syncMode: 'manual'})}
                    className={`p-3 rounded-xl border text-center ${
                      newGroup.syncMode === 'manual'
                        ? 'bg-purple-500/30 border-purple-400/50 text-white'
                        : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-sm font-medium mb-1">Manual</div>
                    <div className="text-xs text-white/70">Upload separate media for each screen</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewGroup({...newGroup, syncMode: 'auto'})}
                    className={`p-3 rounded-xl border text-center ${
                      newGroup.syncMode === 'auto'
                        ? 'bg-purple-500/30 border-purple-400/50 text-white'
                        : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-sm font-medium mb-1">Auto Split</div>
                    <div className="text-xs text-white/70">Automatically split wide content</div>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => setShowNewGroupModal(false)} 
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddGroup} 
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                Create Group
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upload Content Modal */}
      {showUploadModal && selectedGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.3 }} 
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-lg w-full"
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Upload Content</h2>
                <p className="text-white/70">For {selectedGroup.name} ({selectedGroup.totalScreens} screens)</p>
              </div>
              <button 
                onClick={() => setShowUploadModal(false)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="text-white/70" />
              </button>
            </div>
            
            {/* Upload Mode Selection */}
            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-2">
                Upload Mode
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setUploadMode('auto')}
                  className={`p-4 rounded-xl border ${
                    uploadMode === 'auto'
                      ? 'bg-purple-500/30 border-purple-400/50 text-white'
                      : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                  }`}
                >
                  <SafeIcon icon={FiGrid} className="mx-auto text-2xl mb-2" />
                  <div className="text-sm font-medium mb-1">Auto Split</div>
                  <div className="text-xs text-white/70">Upload one wide video that will be split across screens</div>
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('manual')}
                  className={`p-4 rounded-xl border ${
                    uploadMode === 'manual'
                      ? 'bg-purple-500/30 border-purple-400/50 text-white'
                      : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                  }`}
                >
                  <SafeIcon icon={FiList} className="mx-auto text-2xl mb-2" />
                  <div className="text-sm font-medium mb-1">Manual Assignment</div>
                  <div className="text-xs text-white/70">Upload separate content for each screen</div>
                </button>
              </div>
            </div>
            
            {uploadMode === 'auto' ? (
              <div className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center mb-6">
                <SafeIcon icon={FiUpload} className="text-4xl text-white/50 mx-auto mb-4" />
                <p className="text-white/70 mb-2">Upload a wide video to be split across screens</p>
                <p className="text-white/50 text-sm mb-4">Supported formats: MP4, WebM (16:9 aspect ratio recommended)</p>
                <button className="px-4 py-2 bg-purple-500/20 border border-purple-400/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors">
                  Select Video
                </button>
              </div>
            ) : (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3">Upload content for each screen</h3>
                <div className="space-y-3">
                  {selectedGroup.screens.map((screen) => (
                    <div key={screen.id} className="bg-white/5 rounded-lg p-3 flex justify-between items-center">
                      <div className="text-white">{screen.name}</div>
                      <button className="px-3 py-1 bg-purple-500/20 border border-purple-400/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm">
                        Select File
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {uploadMode === 'auto' && (
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiInfo} className="text-blue-400" />
                  <p className="text-white font-medium">Optimal Resolution</p>
                </div>
                <p className="text-white/70 text-sm">
                  For {selectedGroup.totalScreens} screens in {selectedGroup.orientation} orientation, 
                  upload a {selectedGroup.orientation === 'horizontal' ? 
                    `${1920 * selectedGroup.totalScreens}x1080` : 
                    `1920x${1080 * selectedGroup.totalScreens}`} video for best results.
                </p>
              </div>
            )}
            
            <div className="flex space-x-4">
              <button 
                onClick={() => setShowUploadModal(false)} 
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleUploadContent} 
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                Upload Content
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Configure Screens Modal */}
      {showConfigureScreensModal && selectedGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.3 }} 
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-2xl w-full"
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Configure Screens</h2>
                <p className="text-white/70">{selectedGroup.name} ({selectedGroup.totalScreens} screens)</p>
              </div>
              <button 
                onClick={() => setShowConfigureScreensModal(false)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="text-white/70" />
              </button>
            </div>
            
            <div className="space-y-6 mb-6">
              {/* Visual Layout */}
              <div>
                <h3 className="text-white font-medium mb-3">Layout Preview</h3>
                <div className="bg-white/5 p-6 rounded-xl flex items-center justify-center">
                  <div className={`flex ${selectedGroup.orientation === 'horizontal' ? 'flex-row' : 'flex-col'} gap-2`}>
                    {selectedGroup.screens.map((screen) => (
                      <div 
                        key={screen.id} 
                        className={`
                          ${selectedGroup.orientation === 'horizontal' ? 'w-24 h-36' : 'w-36 h-24'} 
                          ${screen.status === 'online' ? 'bg-purple-500/30 border-purple-400/50' : 'bg-white/10 border-white/30'} 
                          border-2 rounded-lg flex flex-col items-center justify-center
                        `}
                      >
                        <div className={`w-3 h-3 rounded-full ${screen.status === 'online' ? 'bg-green-400' : 'bg-red-400'} mb-2`}></div>
                        <span className="text-white text-sm">{screen.name}</span>
                        <span className="text-white/60 text-xs mt-1">#{screen.id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Screen Management */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-white font-medium">Screens</h3>
                  <button 
                    onClick={() => {
                      setShowConfigureScreensModal(false);
                      setShowAddScreenModal(true);
                    }}
                    className="flex items-center space-x-1 text-sm bg-white/10 px-3 py-1 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} className="text-xs" />
                    <span>Add Screen</span>
                  </button>
                </div>
                
                <div className="bg-white/5 rounded-xl overflow-hidden">
                  <table className="w-full text-white">
                    <thead className="bg-white/10">
                      <tr>
                        <th className="py-2 px-4 text-left">#</th>
                        <th className="py-2 px-4 text-left">Name</th>
                        <th className="py-2 px-4 text-left">Status</th>
                        <th className="py-2 px-4 text-left">Pairing Code</th>
                        <th className="py-2 px-4 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {selectedGroup.screens.map((screen) => (
                        <tr key={screen.id} className="hover:bg-white/5">
                          <td className="py-3 px-4">{screen.id}</td>
                          <td className="py-3 px-4">{screen.name}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-1">
                              <div className={`w-2 h-2 rounded-full ${screen.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                              <span>{screen.status}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <span>{screen.pairingCode}</span>
                              <button 
                                onClick={() => copyPairingCode(screen.pairingCode)}
                                className="text-purple-400 hover:text-purple-300"
                              >
                                <SafeIcon icon={FiCopy} className="text-xs" />
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleEditScreenName(selectedGroup.id, screen.id, prompt('Enter new screen name:', screen.name) || screen.name)}
                                className="p-1 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                              >
                                <SafeIcon icon={FiEdit} className="text-white/70 text-sm" />
                              </button>
                              <button 
                                onClick={() => handleDeleteScreen(selectedGroup.id, screen.id)}
                                className="p-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} className="text-red-400 text-sm" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Group Settings */}
              <div>
                <h3 className="text-white font-medium mb-3">Group Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/5 p-4 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Orientation</span>
                      <div className="flex space-x-2">
                        <button 
                          className={`px-3 py-1 rounded-lg text-sm ${selectedGroup.orientation === 'horizontal' ? 'bg-purple-500/30 text-purple-400' : 'bg-white/10 text-white/70'}`}
                          onClick={() => {
                            const updatedGroups = syncGroups.map(group => 
                              group.id === selectedGroup.id ? { ...group, orientation: 'horizontal' } : group
                            );
                            setSyncGroups(updatedGroups);
                            setSelectedGroup({ ...selectedGroup, orientation: 'horizontal' });
                          }}
                        >
                          Horizontal
                        </button>
                        <button 
                          className={`px-3 py-1 rounded-lg text-sm ${selectedGroup.orientation === 'vertical' ? 'bg-purple-500/30 text-purple-400' : 'bg-white/10 text-white/70'}`}
                          onClick={() => {
                            const updatedGroups = syncGroups.map(group => 
                              group.id === selectedGroup.id ? { ...group, orientation: 'vertical' } : group
                            );
                            setSyncGroups(updatedGroups);
                            setSelectedGroup({ ...selectedGroup, orientation: 'vertical' });
                          }}
                        >
                          Vertical
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Sync Mode</span>
                      <div className="flex space-x-2">
                        <button 
                          className={`px-3 py-1 rounded-lg text-sm ${selectedGroup.syncMode === 'manual' ? 'bg-purple-500/30 text-purple-400' : 'bg-white/10 text-white/70'}`}
                          onClick={() => {
                            const updatedGroups = syncGroups.map(group => 
                              group.id === selectedGroup.id ? { ...group, syncMode: 'manual' } : group
                            );
                            setSyncGroups(updatedGroups);
                            setSelectedGroup({ ...selectedGroup, syncMode: 'manual' });
                          }}
                        >
                          Manual
                        </button>
                        <button 
                          className={`px-3 py-1 rounded-lg text-sm ${selectedGroup.syncMode === 'auto' ? 'bg-purple-500/30 text-purple-400' : 'bg-white/10 text-white/70'}`}
                          onClick={() => {
                            const updatedGroups = syncGroups.map(group => 
                              group.id === selectedGroup.id ? { ...group, syncMode: 'auto' } : group
                            );
                            setSyncGroups(updatedGroups);
                            setSelectedGroup({ ...selectedGroup, syncMode: 'auto' });
                          }}
                        >
                          Auto Split
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => setShowConfigureScreensModal(false)} 
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  setShowConfigureScreensModal(false);
                  toast.success('Settings saved successfully!');
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                <div className="flex items-center justify-center space-x-2">
                  <SafeIcon icon={FiCheck} />
                  <span>Save Settings</span>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Screen Modal */}
      {showAddScreenModal && selectedGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.3 }} 
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Add New Screen</h2>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Screen Name
                </label>
                <input 
                  type="text" 
                  value={newScreen.name} 
                  onChange={(e) => setNewScreen({...newScreen, name: e.target.value})} 
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent" 
                  placeholder="e.g., Right Display" 
                />
              </div>

              <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm mb-2">New screen will be added to:</p>
                <p className="text-white font-medium">{selectedGroup.name}</p>
                <p className="text-blue-300/80 text-xs mt-2">
                  This will increase the total screen count to {selectedGroup.totalScreens + 1}
                </p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  setShowAddScreenModal(false);
                  if (showConfigureScreensModal) {
                    setShowConfigureScreensModal(true);
                  }
                }} 
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddScreen} 
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                Add Screen
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SyncGroupManager;